<template>
  <v-container fluid>
    <confirm-delete ref="confirmDelete" />
    <h3 class="text-center">
      Manufacturers
    </h3>
    <v-dialog
      v-model="mergeMfgDialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          <span class="text-center">
            Merge Duplicate Manufacturers
          </span>
        </v-card-title>
        <v-card-subtitle>
          Merging duplicate manufacturers will assign all inventory from
          the source manufacturer to the destination manufacturer,
          then permanently delete the source manufacturer.
        </v-card-subtitle>
        <v-card-text>
          <manufacturer-auto-complete
            :mfg="mergeSrcMfg"
            :locked="false"
            label-text="Source Manufacturer"
            @onClickMfg="setSrcMfg"
          />
          <manufacturer-auto-complete
            :mfg="mergeDestMfg"
            :locked="false"
            label-text="Destination Manufacturer"
            @onClickMfg="setDestMfg"
          />
          <v-btn
            color="error"
            @click="mergeMfgs()"
          >
            Merge and delete
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="mergeMfgDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-data-table
      :headers="headers"
      :items="manufacturers"
      :loading="searchPending"
      :options.sync="options"
      :server-items-length="totalManufacturers"
      :footer-props="{'items-per-page-options': [25, 50, 100]}"
      loading-text="loading, please wait"
      class="elevation-1"
      @click:row="editItem"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-row>
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              label="Search manufacturers"
              clearable
              single-line
              hide-details
            />
            <v-menu
              top
              origin="center center"
              transition="scale-transition"
            >
              <template v-slot:activator="{on, attrs}">
                <v-btn
                  text
                  color="primary"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Actions
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="newMfgDialog = true"
                >
                  New Manufacturer
                </v-list-item>
                <v-list-item
                  @click="mergeMfgDialog = true"
                >
                  Merge Duplicate Manufacturers
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>
        </v-toolbar>
      </template>
      <template v-slot:progress>
        <v-progress-linear
          v-show="searchPending"
          indeterminate
          rounded
          height="10"
        />
      </template>
    </v-data-table>
    <v-dialog
      v-model="newMfgDialog"
      max-width="500px"
    >
      <new-manufacturer-form @manufacturerCreated="addManufacturer" />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import NewManufacturerForm from './NewManufacturerForm.vue';
import Manufacturer from '../models/manufacturer';
import ConfirmDelete from './ConfirmDeleteComponent.vue';
import ManufacturerAutoComplete from './ManufacturerAutoComplete.vue';

export default {
  name: 'Manufacturers',
  components: {
    NewManufacturerForm,
    ConfirmDelete,
    ManufacturerAutoComplete,
  },
  data: () => ({
    noData: false,
    searchPending: false,
    editDialog: false,
    deleteDialog: false,
    newMfgDialog: false,
    mergeMfgDialog: false,
    mergeSuccessDialog: false,
    mergeSrcMfg: null,
    mergeDestMfg: null,
    editedIndex: -1,
    search: '',
    searchTimerId: null,
    searchRequestId: 0,
    tableReady: false,
    totalManufacturers: 0,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['name'],
      sortDesc: [false],
    },
    editedItem: new Manufacturer(),
    confirmDelete: new Manufacturer(),
    manufacturerTypes: [
      'MANUFACTURER',
      'ANTIQUE',
      'SHOP',
    ],
    headers: [
      {
        text: 'Name',
        value: 'name',
      },
      {
        text: 'Type',
        value: 'manufacturerType',
      },
      {
        text: '',
        value: 'actions',
        sortable: false,
      },
    ],
    manufacturers: [],
  }),
  computed: {
    formTitle() {
      return `${this.editedItem.name}`;
    },
    ...mapGetters({
      isLoading: 'apiClient/showLoading',
      hasError: 'apiClient/showError',
    }),
  },
  watch: {
    editDialog(val) {
      this.editDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
    deleteDialog(val) {
      this.deleteDialog = val;

      if (!val) {
        this.dismissError();
      }
    },
    search() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.applySearch();
      }, 400);
    },
    options: {
      handler() {
        if (this.tableReady) {
          this.getManufacturers();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.tableReady = true;
    this.getManufacturers();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimerId);
  },
  methods: {
    ...mapActions('apiClient', ['get', 'create', 'update', 'del']),
    ...mapMutations('apiClient', ['dismissError']),
    closeEditDialog() {
      this.editDialog = false;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
    },
    editItem(item) {
      this.$router.push({ name: 'mfgEdit', params: { mfgId: item.manufacturerId } });
    },
    addManufacturer() {
      this.newMfgDialog = false;
      if (this.options.page === 1) {
        this.getManufacturers();
      } else {
        this.options.page = 1;
      }
    },
    setSrcMfg(mfg) {
      this.mergeSrcMfg = mfg;
    },
    setDestMfg(mfg) {
      this.mergeDestMfg = mfg;
    },
    async getManufacturers() {
      this.searchRequestId += 1;
      const requestId = this.searchRequestId;
      this.searchPending = true;

      const page = Math.max(this.options.page - 1, 0);
      const size = this.options.itemsPerPage;
      const sortBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : 'name';
      const sortDesc = this.options.sortDesc.length > 0 ? this.options.sortDesc[0] : false;
      const searchText = this.search ? this.search.trim() : '';

      await this.get({
        url: '/manufacturers',
        params: {
          page,
          size,
          sortBy,
          sortDesc,
          searchText: searchText || null,
        },
      }).then(
        (response) => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          this.manufacturers = response.data.data;
          this.totalManufacturers = response.data.totalElements;
        },
        (error) => {
          console.log(error);
        },
      );

      if (requestId === this.searchRequestId) {
        this.searchPending = false;
      }
    },
    applySearch() {
      clearTimeout(this.searchTimerId);
      this.searchTimerId = null;

      if (this.options.page === 1) {
        this.getManufacturers();
      } else {
        this.options.page = 1;
      }
    },
    async save(item) {
      await this.update({ url: '/manufacturers', body: item, id: item.manufacturerId }).then(
        (response) => {
          this.manufacturers[this.editedIndex] = response.data;
          this.editedItem = item;
          this.editDialog = false;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    showConfirmDelete(item) {
      this.$refs.confirmDelete.open(item.name).then((selection) => {
        if (selection) {
          this.deleteItem(item);
        }
      });
    },
    async deleteItem(item) {
      await this.del({ url: '/manufacturers', id: item.manufacturerId }).then(
        (response) => {
          this.manufacturers.pop(response.data);
          this.deleteDialog = false;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async mergeMfgs() {
      if (this.mergeSrcMfg.manufacturerId && this.mergeDestMfg.manufacturerId) {
        await this.create({ url: `/manufacturers/merge/${this.mergeSrcMfg.manufacturerId}/${this.mergeDestMfg.manufacturerId}` }).then(
          () => {
            this.mergeMfgDialog = false;
            this.mergeSuccessDialog = true;
            this.getManufacturers();
          },
          (error) => {
            console.log(error);
          },
        );
      }
    },
  },
};
</script>
